import { useState } from 'react'
import ListingCard from '../ListingCard/ListingCard'
import Footer from '../Footer/Footer'
import './DetailPage.css'

// Formate une date ISO en AAAA-MM-JJ
const fmtDate = (iso) => {
  if (!iso) return '—'
  try { 
    return new Date(iso).toISOString().slice(0, 10) // "2026-06-28" → "2026-06-28"
  } catch { 
    return '—' 
  }
}

export default function DetailPage({
  listing       = null,
  allListings   = [],
  wishlist      = [],
  onBack        = () => {},
  onFav         = () => {},
  onContact     = () => {},
  onDetailClick = () => {},
  onAddCart     = () => {},
  isLoggedIn    = false,
  onAuthOpen    = () => {},
  onCategoryClick = () => {},
}) {
  const [activeImg, setActiveImg] = useState(0)

  if (!listing) return (
    <div className="detail-empty">
      <div className="detail-empty-icon">🔍</div>
      <div className="detail-empty-title">Annonce introuvable</div>
      <button className="detail-empty-btn" onClick={onBack}>← Retour</button>
    </div>
  )

  // Images : priorité à images[] (Cloudinary), puis imgs[], puis img
  const imgs = listing.images?.length > 0
    ? listing.images.map(i => i.url)
    : listing.imgs?.length > 0
      ? listing.imgs
      : listing.img
        ? [listing.img]
        : []

  const hasImages = imgs.length > 0
  const isFav = wishlist.includes(listing.id)

  const similar = allListings
    .filter(l => l.category === listing.category && l.id !== listing.id && l.status === 'active')
    .slice(0, 3)

  const fmt = (n) => n === 0 ? 'Gratuit' : n.toLocaleString('fr-FR') + ' €'
  const prevImg = () => setActiveImg(i => i === 0 ? imgs.length - 1 : i - 1)
  const nextImg = () => setActiveImg(i => i === imgs.length - 1 ? 0 : i + 1)

  // Date : createdAt (MongoDB) en priorité, sinon date (champ legacy)
  const dateAffichee = fmtDate(listing.createdAt || listing.date)

  // Statut lisible
  const statusLabel = listing.status === 'sold' ? 'Vendu' : 'Disponible'
  const statusColor = listing.status === 'sold' ? '#E63946' : '#4CAF50'

  // Requête Maps : ville + région + France
  const mapQuery = [listing.city, listing.region, 'France'].filter(Boolean).join(', ')

  return (
    <div className="detail-page">

      {/* Fil d'ariane */}
      <div className="detail-breadcrumb">
        <span className="detail-bc-link" onClick={onBack}>Accueil</span>
        <span className="detail-bc-sep">›</span>
        <span className="detail-bc-link" onClick={onBack}>{listing.category}</span>
        <span className="detail-bc-sep">›</span>
        <span>{listing.breed}</span>
      </div>

      <button className="detail-back" onClick={onBack}>← Retour aux annonces</button>

      <div className="detail-layout">

        {/* ── Colonne gauche ── */}
        <div className="detail-left">

          {/* Galerie */}
          {hasImages ? (
            <div className="detail-gallery">
              <div className="detail-main-wrap">
                <img className="detail-main-img" src={imgs[activeImg]} alt={listing.title} loading="lazy" />
                <div className="detail-img-counter">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                  {activeImg + 1} / {imgs.length}
                </div>
                {imgs.length > 1 && (
                  <>
                    <button className="detail-arrow detail-arrow-left" onClick={prevImg}>‹</button>
                    <button className="detail-arrow detail-arrow-right" onClick={nextImg}>›</button>
                  </>
                )}
              </div>
              {imgs.length > 1 && (
                <div className="detail-thumbs">
                  {imgs.map((im, i) => (
                    <img
                      key={i}
                      className={`detail-thumb${activeImg === i ? ' active' : ''}`}
                      src={im}
                      alt={listing.title}
                      onClick={() => setActiveImg(i)}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="detail-gallery">
              <div className="detail-no-img">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color:'var(--slate-lt)' }}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                <div style={{ fontSize:13, color:'var(--slate-lt)', marginTop:8 }}>Aucune photo disponible</div>
              </div>
            </div>
          )}

          {/* Description */}
          <div className="detail-section">
            <div className="detail-section-title">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
              Description de l'annonce
            </div>
            <p className="detail-desc">{listing.desc || 'Aucune description fournie.'}</p>
          </div>

          {/* Carte Google Maps */}
          <div className="detail-section">
            <div className="detail-section-title">
              📍 Localisation — {listing.city}{listing.region ? `, ${listing.region}` : ''}
            </div>
            <div className="detail-map-container">
              <iframe
                className="detail-map-iframe"
                title="Localisation"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&t=&z=11&ie=UTF8&iwloc=&output=embed`}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="detail-map-address">
              📍 {listing.city}{listing.region ? `, ${listing.region}` : ''}, France
            </div>
          </div>
        </div>

        {/* ── Colonne droite ── */}
        <div className="detail-right">
          <div className="detail-info-card">
            <div className="detail-breed-tag">{listing.breed} · {listing.category}</div>
            <h1 className="detail-name">{listing.title}</h1>
            <div className="detail-loc">📍 {listing.city}{listing.region ? `, ${listing.region}` : ''}</div>
            <div className={`detail-price${listing.status === 'sold' ? ' detail-price-sold' : ''}`}>{fmt(listing.price)}</div>
            <div className="detail-price-note">{listing.status === 'sold' ? 'Animal adoptÃ©' : 'Livraison internationale incluse'}</div>

            <div className="detail-chips">
              <span className="detail-chip">🎂 {listing.age}</span>
              <span className="detail-chip detail-chip-icon">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M6 20v-2a6 6 0 0 1 12 0v2"/></svg>
                {listing.sex}
              </span>
              <span className="detail-chip detail-chip-icon">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                {listing.views} vues
              </span>
              {/* Date depuis createdAt MongoDB - Format AAAA-MM-JJ */}
              <span className="detail-chip">📅 {dateAffichee}</span>
            </div>

            <div className="detail-health">
              <span className={`detail-hpill ${listing.vaccinated ? 'hp-yes' : 'hp-no'}`}>{listing.vaccinated ? '✓' : '✗'} Vacciné</span>
              <span className={`detail-hpill ${listing.microchipped ? 'hp-yes' : 'hp-no'}`}>{listing.microchipped ? '✓' : '✗'} Pucé</span>
              <span className={`detail-hpill ${listing.pedigree ? 'hp-yes' : 'hp-no'}`}>{listing.pedigree ? '✓' : '✗'} Pédigrée</span>
            </div>

            <div className="detail-specs">
              {[
                ['Âge',    listing.age],
                ['Sexe',   listing.sex],
                ['Race',   listing.breed],
                ['Ville',  listing.city],
                ['Région', listing.region || '—'],
                ['Statut', statusLabel],
              ].map(([label, value]) => (
                <div key={label} className="detail-spec-box">
                  <div className="detail-spec-label">{label}</div>
                  <div className="detail-spec-value" style={label === 'Statut' ? { color: statusColor, fontWeight: 700 } : {}}>
                    {value}
                  </div>
                </div>
              ))}
            </div>

            {/* ── Bloc annonceur ── */}
            <div className="detail-seller">
              <div className="detail-seller-title">Annonceur</div>
              <div className="detail-seller-row">
                <div className="detail-seller-name">
                  {listing.seller || 'Animaux pour Adoption de la France'}
                </div>
                {listing.sellerType && (
                  <span className={`detail-seller-badge ${listing.sellerType === 'Pro' ? 'dsb-pro' : 'dsb-part'}`}>
                    {listing.sellerType === 'Pro' ? 'Éleveur Pro' : 'Particulier'}
                  </span>
                )}
              </div>

              {/* Contacts — visibles uniquement si connecté */}
              {isLoggedIn ? (
                <div className="detail-seller-contacts">
                  {listing.phone && (
                    <div className="detail-contact-row">
                      <span className="detail-contact-icon">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.36 13.4 19.79 19.79 0 0 1 1.15 4.84 2 2 0 0 1 3.12 2.68h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 10a16 16 0 0 0 6.29 6.29l1.42-1.42a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 17z"/></svg>
                      </span>
                      <a className="detail-contact-value" href={`tel:${listing.phone}`}>{listing.phone}</a>
                    </div>
                  )}
                  {listing.email && (
                    <div className="detail-contact-row">
                      <span className="detail-contact-icon">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                      </span>
                      <a className="detail-contact-value" href={`mailto:${listing.email}`}>{listing.email}</a>
                    </div>
                  )}
                  {listing.website && (
                    <div className="detail-contact-row">
                      <span className="detail-contact-icon">🌐</span>
                      <span className="detail-contact-value">{listing.website}</span>
                    </div>
                  )}
                  {!listing.phone && !listing.email && !listing.website && (
                    <div style={{ fontSize:12, color:'var(--slate-lt)', marginTop:6 }}>
                      Aucune coordonnée renseignée pour cette annonce.
                    </div>
                  )}
                </div>
              ) : (
                <div className="detail-seller-contacts">
                  <div className="detail-contact-row detail-contact-blurred">
                    <span className="detail-contact-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.36 13.4 19.79 19.79 0 0 1 1.15 4.84 2 2 0 0 1 3.12 2.68h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 10a16 16 0 0 0 6.29 6.29l1.42-1.42a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 17z"/></svg>
                    </span>
                    <span className="detail-contact-masked">06 •• •• •• ••</span>
                    <button className="detail-contact-login-btn" onClick={onAuthOpen}>Se connecter</button>
                  </div>
                  <div className="detail-contact-row detail-contact-blurred">
                    <span className="detail-contact-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                    </span>
                    <span className="detail-contact-masked">••••••@••••••.••</span>
                  </div>
                </div>
              )}
            </div>

            <button className="detail-contact-btn" onClick={() => isLoggedIn ? onContact(listing) : onAuthOpen()}>
              {isLoggedIn ? (
                <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.36 13.4 19.79 19.79 0 0 1 1.15 4.84 2 2 0 0 1 3.12 2.68h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 10a16 16 0 0 0 6.29 6.29l1.42-1.42a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 17z"/></svg> Contacter l'annonceur</>
              ) : (
                <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg> Se connecter pour contacter</>
              )}
            </button>
            {listing.status !== 'sold' && listing.sellerType !== 'Don' && (
              <button className="detail-cart-btn" onClick={() => onAddCart(listing)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/></svg>
                Ajouter au panier
              </button>
            )}
            <button className="detail-fav-btn" onClick={() => onFav(listing.id)}>
              {isFav ? (
                <><svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> Sauvegardé</>
              ) : (
                <><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> Sauvegarder dans mes favoris</>
              )}
            </button>
          </div>
        </div>
      </div>

      {similar.length > 0 && (
        <div className="detail-similar">
          <div className="detail-similar-title">Annonces similaires</div>
          <div className="detail-similar-grid">
            {similar.map(item => (
              <ListingCard
                key={item.id}
                listing={item}
                isFav={wishlist.includes(item.id)}
                onFav={() => onFav(item.id)}
                onDetail={() => onDetailClick(item.id)}
                onAddCart={onAddCart}
              />
            ))}
          </div>
        </div>
      )}

      <Footer onCategoryClick={onCategoryClick} />
    </div>
  )
}
